import moment from 'moment';
import { Employee } from 'src/utils/interfaces/interfaces';
import { useTranslation } from 'react-i18next';
import {
  FaCalendarDays,
  FaEnvelope,
  FaGlobe,
  FaPhone,
  FaRegIdBadge,
} from 'src/icons';

type IEmployeeProps = { Employee: Employee };

export default function EmployeeCard({ Employee }: IEmployeeProps) {
  const { t } = useTranslation();
  const employeeName =
    `${Employee?.firstName ?? ''} ${Employee?.lastName ?? ''}`.trim() || 'N/A';
  const initials =
    `${Employee?.firstName?.[0] ?? ''}${Employee?.lastName?.[0] ?? ''}`.toUpperCase() ||
    'NA';

  const detailItems = [
    {
      label: t('user.contact_number'),
      value: Employee?.phone || 'N/A',
      icon: <FaPhone className="text-base" />,
    },
    {
      label: t('user.email'),
      value: Employee?.email || 'N/A',
      icon: <FaEnvelope className="text-base" />,
    },
    {
      label: t('employee.id_no'),
      value: Employee?.idNumber || 'N/A',
      icon: <FaRegIdBadge className="text-base" />,
    },
    {
      label: t('employee.id_expiry'),
      value: Employee?.idExpiry
        ? moment(Employee.idExpiry).format('Do MMM YYYY, h:mm a')
        : 'N/A',
      icon: <FaCalendarDays className="text-base" />,
    },
    {
      label: t('user.nationality'),
      value: Employee?.nationality || 'N/A',
      icon: <FaGlobe className="text-base" />,
    },
  ];

  return (
    <div
      className="rounded-lg border px-6 py-4 shadow-card"
      style={{
        backgroundColor: 'var(--bg-surface-2)',
        borderColor: 'var(--border-light)',
      }}
    >
      <div
        className="py-4 text-center border-b"
        style={{ borderColor: 'var(--border-light)' }}
      >
        <div
          className="rounded-full w-fit mx-auto px-5 py-4 mb-2"
          style={{
            backgroundColor: 'var(--color-primary-light)',
            color: 'var(--color-primary)',
            border: '1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)',
          }}
        >
          <p className="pt-1 text-3xl font-bold text-center place-self-center">
            {initials}
          </p>
        </div>
        <p className="text-lg capitalize" style={{ color: 'var(--text-main)' }}>
          {employeeName}
        </p>
      </div>
      {detailItems.map((item) => (
        <div
          key={item.label}
          className="grid grid-cols-6 py-4 border-b"
          style={{ borderColor: 'var(--border-light)' }}
        >
          <div
            className="my-auto mx-auto flex h-9 w-9 items-center justify-center rounded-lg"
            style={{
              backgroundColor: 'var(--color-primary-light)',
              color: 'var(--color-primary)',
            }}
          >
            {item.icon}
          </div>
          <div className="col-span-5">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {item.label}
            </p>
            <p className="text-sm break-words" style={{ color: 'var(--text-main)' }}>
              {item.value}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
